/**
 * The first-visit **loading overlay**.
 *
 * On a fresh visit there is a gap before the bank is renderable: the Pixi asset
 * atlases (body/equip frames, land/static textures, the hue table + palette, the
 * baked overhead font) have to load, and the spectator backdrop connection has to
 * deliver its first world snapshot so `main.ts` can build the renderer. During that
 * gap the `#app` canvas is an empty near-black fill behind the splash, so without
 * a loading state the visitor faces a half-loaded screen with no signal that
 * anything is happening.
 *
 * This overlay is declared **visible in `index.html`** (not `hidden`), so it paints
 * the instant the page does - before the JS bundle even parses - and sits **above
 * the splash** (`z-index` over the login form). `main.ts` dismisses it through
 * {@link LoadingController.ready} exactly when the world is genuinely ready to draw
 * (assets resolved **and** the first snapshot has rendered a frame), so there is no
 * flash of empty map and no overlay left stuck up. If asset loading or the initial
 * connection fails, {@link LoadingController.fail} flips the overlay to an
 * error/retry state instead of spinning forever.
 *
 * Like `splash.ts`/`hud.ts`/`chat.ts` this module owns only the DOM wiring; the
 * markup and styling live in `index.html`. It reads no Pixi and no socket.
 */

/** Optional hooks for {@link initLoading}; all optional so headless tests pass none. */
export interface LoadingOptions {
  /** The document to wire (defaults to the live `document`); injected by tests. */
  doc?: Document;
  /**
   * What the error-state **Retry** button does. Defaults to a full page reload,
   * which re-runs `main()` from scratch (re-showing this overlay and re-attempting
   * asset load + connection) - the simplest reliable retry. Injected by tests so a
   * click is observable without navigating the jsdom window.
   */
  onRetry?: () => void;
}

/** The handle `main.ts` drives the loading overlay through. */
export interface LoadingController {
  /**
   * Dismiss the overlay: the world is ready to render. Idempotent and **final** -
   * once dismissed the overlay stays gone, so a late {@link fail} (e.g. a stale
   * connection timeout that fires after the world already came up) is ignored.
   */
  ready(): void;
  /**
   * Flip the overlay to its error/retry state with `message` (asset load or the
   * initial connection failed). A no-op once {@link ready} has dismissed the
   * overlay, so a genuine success always wins over a late failure signal.
   */
  fail(message: string): void;
}

/**
 * Wire the `#loading` overlay (declared **visible** in `index.html`) into a handle
 * `main.ts` dismisses on first render or flips to an error/retry state on failure.
 * The normal loading state is the spinner alone — no *visible* status text
 * (ADR-0085) — backed by an sr-only `#loading-status` label so the role=status
 * region still announces "loading" to screen readers; the only copy this module
 * ever sets is the error message. The element and its parts (`#loading-spinner`,
 * `#loading-status`, `#loading-error`, `#loading-error-message`, `#loading-retry`)
 * are looked up once; any that are missing are tolerated (headless tests mount a
 * subset), so partial markup never throws. Returns a no-op handle when the overlay
 * element itself is absent.
 */
export function initLoading(opts: LoadingOptions = {}): LoadingController {
  const doc = opts.doc ?? document;
  const onRetry = opts.onRetry ?? (() => location.reload());
  const root = doc.getElementById('loading');
  const spinner = doc.getElementById('loading-spinner');
  const status = doc.getElementById('loading-status');
  const error = doc.getElementById('loading-error');
  const errorMessage = doc.getElementById('loading-error-message');
  const retry = doc.getElementById('loading-retry');

  // The overlay's terminal state. Once `ready` dismisses it, a later `fail` is
  // ignored so a success can never be clobbered by a stale failure signal.
  let dismissed = false;

  retry?.addEventListener('click', () => onRetry());

  return {
    ready() {
      dismissed = true;
      if (root) root.hidden = true;
    },
    fail(message) {
      if (dismissed || !root) return;
      // Swap the spinner (and the sr-only "loading" label) for the error block; keep
      // the overlay up so the visitor sees why and can retry rather than staring at a
      // dead spinner. Dropping the label leaves the error message as the sole
      // announcement in the role=status region.
      if (spinner) spinner.hidden = true;
      if (status) status.hidden = true;
      if (errorMessage) errorMessage.textContent = message;
      if (error) error.hidden = false;
    },
  };
}
